import * as THREE from 'three';
import { PieceType } from '../core/types.ts';

/**
 * Creates high-detail procedural wizard chess piece geometries.
 * Uses composite Three.js shapes to form authentic fantasy silhouettes.
 */
export class PieceGeometryBuilder {
  private static cache: Map<PieceType, THREE.BufferGeometry> = new Map();

  public static getGeometry(type: PieceType): THREE.BufferGeometry {
    if (this.cache.has(type)) {
      return this.cache.get(type)!.clone();
    }

    let geom: THREE.BufferGeometry;
    switch (type) {
      case 'p':
        geom = this.createPawnGeometry();
        break;
      case 'n':
        geom = this.createKnightGeometry();
        break;
      case 'b':
        geom = this.createBishopGeometry();
        break;
      case 'r':
        geom = this.createRookGeometry();
        break;
      case 'q':
        geom = this.createQueenGeometry();
        break;
      case 'k':
        geom = this.createKingGeometry();
        break;
      default:
        geom = new THREE.CylinderGeometry(0.3, 0.4, 1.2, 16);
    }

    this.cache.set(type, geom);
    return geom.clone();
  }

  // --- Common Bases ---
  private static createBaseGeometries(): THREE.BufferGeometry[] {
    const parts: THREE.BufferGeometry[] = [];

    // Bottom tiered plinth
    const b1 = new THREE.CylinderGeometry(0.68, 0.74, 0.16, 24);
    b1.translate(0, 0.08, 0);
    parts.push(b1);

    // Mid base bevel
    const b2 = new THREE.CylinderGeometry(0.56, 0.68, 0.14, 24);
    b2.translate(0, 0.22, 0);
    parts.push(b2);

    // Torus ring trim
    const b3 = new THREE.TorusGeometry(0.53, 0.05, 12, 24);
    b3.rotateX(Math.PI / 2);
    b3.translate(0, 0.3, 0);
    parts.push(b3);

    return parts;
  }

  // --- PAWN ---
  // Heavy stone guardian with helmet, shoulder collar, and crest
  private static createPawnGeometry(): THREE.BufferGeometry {
    const parts = this.createBaseGeometries();

    // Body taper
    const body = new THREE.CylinderGeometry(0.35, 0.52, 0.7, 20);
    body.translate(0, 0.68, 0);
    parts.push(body);

    // Neck ring
    const ring = new THREE.TorusGeometry(0.36, 0.04, 12, 20);
    ring.rotateX(Math.PI / 2);
    ring.translate(0, 1.05, 0);
    parts.push(ring);

    // Helmet head
    const head = new THREE.SphereGeometry(0.34, 20, 16);
    head.scale(1, 1.15, 1);
    head.translate(0, 1.38, 0);
    parts.push(head);

    // Helmet visor peak
    const visor = new THREE.ConeGeometry(0.12, 0.24, 12);
    visor.rotateX(-Math.PI / 3);
    visor.translate(0, 1.45, 0.3);
    parts.push(visor);

    // Crown spike
    const spike = new THREE.ConeGeometry(0.09, 0.22, 12);
    spike.translate(0, 1.82, 0);
    parts.push(spike);

    return this.mergeGeometries(parts);
  }

  // --- KNIGHT ---
  // Armored mythical warhorse head with chiseled mane and war crest
  private static createKnightGeometry(): THREE.BufferGeometry {
    const parts = this.createBaseGeometries();

    // Lower neck / chest barding
    const chest = new THREE.CylinderGeometry(0.42, 0.54, 0.6, 20);
    chest.translate(0, 0.62, 0);
    parts.push(chest);

    // Arched neck angled forward
    const neck = new THREE.CylinderGeometry(0.32, 0.44, 0.75, 16);
    neck.rotateX(0.3);
    neck.translate(0, 1.12, 0.1);
    parts.push(neck);

    // Horse muzzle/jaw
    const muzzle = new THREE.BoxGeometry(0.44, 0.42, 0.65);
    muzzle.rotateX(0.35);
    muzzle.translate(0, 1.42, 0.38);
    parts.push(muzzle);

    // Nostril slope
    const nose = new THREE.CylinderGeometry(0.2, 0.24, 0.35, 12);
    nose.rotateX(0.7);
    nose.translate(0, 1.25, 0.65);
    parts.push(nose);

    // Armored chiseled mane crest
    const mane = new THREE.BoxGeometry(0.14, 0.95, 0.35);
    mane.rotateX(-0.25);
    mane.translate(0, 1.42, -0.22);
    parts.push(mane);

    // Pointed war ears
    const earL = new THREE.ConeGeometry(0.08, 0.28, 8);
    earL.rotateZ(-0.2);
    earL.translate(-0.16, 1.85, 0.05);
    parts.push(earL);

    const earR = new THREE.ConeGeometry(0.08, 0.28, 8);
    earR.rotateZ(0.2);
    earR.translate(0.16, 1.85, 0.05);
    parts.push(earR);

    // Glowing warhorn/forehead crest
    const crest = new THREE.ConeGeometry(0.07, 0.32, 8);
    crest.rotateX(0.4);
    crest.translate(0, 1.78, 0.32);
    parts.push(crest);

    return this.mergeGeometries(parts);
  }

  // --- BISHOP ---
  // Mystical hooded sorcerer mitre with arcane collar and crystal orb finial
  private static createBishopGeometry(): THREE.BufferGeometry {
    const parts = this.createBaseGeometries();

    // Robe lower body
    const lowerBody = new THREE.CylinderGeometry(0.42, 0.55, 0.65, 20);
    lowerBody.translate(0, 0.65, 0);
    parts.push(lowerBody);

    // Mid collar flare
    const collar = new THREE.TorusGeometry(0.44, 0.06, 12, 20);
    collar.rotateX(Math.PI / 2);
    collar.translate(0, 1.02, 0);
    parts.push(collar);

    // Torso / robe column
    const torso = new THREE.CylinderGeometry(0.34, 0.42, 0.55, 20);
    torso.translate(0, 1.32, 0);
    parts.push(torso);

    // Neck ring
    const neckRing = new THREE.TorusGeometry(0.35, 0.05, 12, 20);
    neckRing.rotateX(Math.PI / 2);
    neckRing.translate(0, 1.62, 0);
    parts.push(neckRing);

    // Mitre / Sorcerer cowl (pointed oval)
    const mitre = new THREE.SphereGeometry(0.38, 20, 16);
    mitre.scale(0.85, 1.5, 0.85);
    mitre.translate(0, 2.12, 0);
    parts.push(mitre);

    // Slit cutout accent (horizontal ring)
    const slitBand = new THREE.TorusGeometry(0.32, 0.03, 8, 20);
    slitBand.rotateX(Math.PI / 4);
    slitBand.translate(0, 2.15, 0);
    parts.push(slitBand);

    // Top mystical arcane crystal orb
    const crystal = new THREE.OctahedronGeometry(0.14, 0);
    crystal.translate(0, 2.76, 0);
    parts.push(crystal);

    return this.mergeGeometries(parts);
  }

  // --- ROOK ---
  // Ancient stone fortress bastion with four crenellated turrets and arrow slits
  private static createRookGeometry(): THREE.BufferGeometry {
    const parts = this.createBaseGeometries();

    // Heavy fortress base
    const fortressBase = new THREE.CylinderGeometry(0.52, 0.62, 0.45, 24);
    fortressBase.translate(0, 0.54, 0);
    parts.push(fortressBase);

    // Main stone tower column
    const tower = new THREE.CylinderGeometry(0.44, 0.5, 0.95, 24);
    tower.translate(0, 1.22, 0);
    parts.push(tower);

    // Tower capital / corbel cornice
    const cornice = new THREE.CylinderGeometry(0.58, 0.44, 0.22, 24);
    cornice.translate(0, 1.78, 0);
    parts.push(cornice);

    // Upper parapet rim
    const parapet = new THREE.CylinderGeometry(0.58, 0.58, 0.32, 24);
    parapet.translate(0, 2.02, 0);
    parts.push(parapet);

    // 4 Crenellated Turret battlements
    for (let i = 0; i < 4; i++) {
      const angle = (i * Math.PI) / 2;
      const battlement = new THREE.BoxGeometry(0.22, 0.26, 0.2);
      const rad = 0.48;
      battlement.translate(Math.cos(angle) * rad, 2.26, Math.sin(angle) * rad);
      battlement.rotateY(-angle);
      parts.push(battlement);
    }

    return this.mergeGeometries(parts);
  }

  // --- QUEEN ---
  // Majestic Sorceress Queen with spiked crown, royal dress, and arcane orb
  private static createQueenGeometry(): THREE.BufferGeometry {
    const parts = this.createBaseGeometries();

    // Lower gown bell
    const gown = new THREE.CylinderGeometry(0.48, 0.62, 0.7, 24);
    gown.translate(0, 0.68, 0);
    parts.push(gown);

    // Waist girdle ring
    const waist = new THREE.TorusGeometry(0.44, 0.05, 12, 24);
    waist.rotateX(Math.PI / 2);
    waist.translate(0, 1.05, 0);
    parts.push(waist);

    // Regal bodice
    const bodice = new THREE.CylinderGeometry(0.38, 0.44, 0.7, 24);
    bodice.translate(0, 1.42, 0);
    parts.push(bodice);

    // Imperial cape collar
    const collar = new THREE.CylinderGeometry(0.52, 0.36, 0.24, 24);
    collar.translate(0, 1.88, 0);
    parts.push(collar);

    // Neck
    const neck = new THREE.CylinderGeometry(0.24, 0.28, 0.22, 16);
    neck.translate(0, 2.08, 0);
    parts.push(neck);

    // Crown coronet bowl
    const crownBowl = new THREE.CylinderGeometry(0.48, 0.28, 0.42, 20);
    crownBowl.translate(0, 2.38, 0);
    parts.push(crownBowl);

    // 8 Crown spikes
    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4;
      const spike = new THREE.ConeGeometry(0.06, 0.26, 8);
      const rad = 0.46;
      spike.translate(Math.cos(angle) * rad, 2.68, Math.sin(angle) * rad);
      parts.push(spike);
    }

    // Top jewel orb
    const orb = new THREE.SphereGeometry(0.16, 16, 16);
    orb.translate(0, 2.76, 0);
    parts.push(orb);

    return this.mergeGeometries(parts);
  }

  // --- KING ---
  // Imperious Stone Monarch with ornate cross crown and heavy pauldrons
  private static createKingGeometry(): THREE.BufferGeometry {
    const parts = this.createBaseGeometries();

    // Heavy mantle base
    const mantleBase = new THREE.CylinderGeometry(0.52, 0.66, 0.75, 24);
    mantleBase.translate(0, 0.7, 0);
    parts.push(mantleBase);

    // Royal belt ring
    const belt = new THREE.TorusGeometry(0.48, 0.06, 12, 24);
    belt.rotateX(Math.PI / 2);
    belt.translate(0, 1.1, 0);
    parts.push(belt);

    // Imperial chest armor
    const chest = new THREE.CylinderGeometry(0.42, 0.48, 0.8, 24);
    chest.translate(0, 1.54, 0);
    parts.push(chest);

    // Heavy imperial pauldrons / mantle cape
    const mantleCape = new THREE.CylinderGeometry(0.62, 0.42, 0.28, 24);
    mantleCape.translate(0, 2.04, 0);
    parts.push(mantleCape);

    // Royal neck collar
    const neck = new THREE.CylinderGeometry(0.28, 0.32, 0.22, 16);
    neck.translate(0, 2.26, 0);
    parts.push(neck);

    // Imperial Crown arches
    const crownBase = new THREE.CylinderGeometry(0.52, 0.42, 0.44, 20);
    crownBase.translate(0, 2.56, 0);
    parts.push(crownBase);

    // Crown cross finial vertical
    const crossV = new THREE.BoxGeometry(0.08, 0.36, 0.08);
    crossV.translate(0, 2.96, 0);
    parts.push(crossV);

    // Crown cross finial horizontal
    const crossH = new THREE.BoxGeometry(0.26, 0.08, 0.08);
    crossH.translate(0, 2.98, 0);
    parts.push(crossH);

    return this.mergeGeometries(parts);
  }

  // Helper to merge BufferGeometries into a single unified mesh buffer
  private static mergeGeometries(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
    // Calculate total attributes
    let totalPositions = 0;
    let totalNormals = 0;

    const converted = geometries.map((g) => {
      let nonIndexed = g;
      if (g.index) {
        nonIndexed = g.toNonIndexed();
      }
      totalPositions += nonIndexed.attributes.position.array.length;
      if (nonIndexed.attributes.normal) {
        totalNormals += nonIndexed.attributes.normal.array.length;
      }
      return nonIndexed;
    });

    const mergedPositions = new Float32Array(totalPositions);
    const mergedNormals = new Float32Array(totalPositions); // match position length

    let posOffset = 0;
    let normOffset = 0;

    for (const g of converted) {
      const pos = g.attributes.position.array as Float32Array;
      mergedPositions.set(pos, posOffset);
      posOffset += pos.length;

      if (g.attributes.normal) {
        const norm = g.attributes.normal.array as Float32Array;
        mergedNormals.set(norm, normOffset);
        normOffset += norm.length;
      }
    }

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(mergedPositions, 3));
    if (normOffset > 0) {
      merged.setAttribute('normal', new THREE.BufferAttribute(mergedNormals, 3));
    } else {
      merged.computeVertexNormals();
    }

    return merged;
  }
}
